import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, Play, Pause, SkipBack, SkipForward, MoreHorizontal, Heart, Share, Trash2 } from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { format } from "date-fns";
import type { VoiceMemo } from "../../types";

interface VoiceMemosAppProps {
  onClose: () => void;
}

export function VoiceMemosApp({ onClose }: VoiceMemosAppProps) {
  const [selectedMemo, setSelectedMemo] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Fetch voice memos from Convex
  const memos = useQuery(api.voicememos.getAll) || [];

  // Format duration as MM:SS
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Play/Pause memo
  const togglePlay = (memo: VoiceMemo) => {
    if (selectedMemo === memo._id) {
      if (isPlaying) {
        audioRef.current?.pause();
        setIsPlaying(false);
      } else {
        audioRef.current?.play();
        setIsPlaying(true);
      }
    } else {
      // New memo selected
      setSelectedMemo(memo._id);
      setDuration(memo.duration);
      setCurrentTime(0);

      if (audioRef.current) {
        audioRef.current.src = memo.url;
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  // Generate simple waveform visualization
  const generateWaveform = (memoId: string) => {
    // In a real app, you'd use actual waveform data
    // For now, generate a deterministic pattern based on ID
    const bars = 40;
    const seed = memoId.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);

    return Array.from({ length: bars }, (_, i) => {
      const height = Math.abs(Math.sin((seed + i) * 0.5)) * 100;
      return { height: Math.max(10, height), index: i };
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 100 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 100 }}
      className="absolute inset-0 z-40 flex flex-col bg-black"
    >
      {/* Hidden Audio Element */}
      <audio ref={audioRef} preload="metadata" />

      {/* Status Bar */}
      <div className="flex items-center justify-between px-6 pt-3 text-white">
        <span className="text-sm font-semibold">9:41</span>
        <div className="flex items-center gap-1">
          <div className="h-4 w-4 rounded-full border-2 border-white" />
          <div className="h-4 w-4 rounded-full border-2 border-white" />
          <div className="h-2 w-6 rounded-sm border-2 border-white bg-white" />
        </div>
      </div>

      {/* Navigation Bar */}
      <div className="flex items-center justify-between px-4 py-3">
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={onClose}
          className="flex items-center gap-1 text-ios-blue"
        >
          <ChevronLeft className="h-6 w-6" />
          <span className="text-lg">Memories</span>
        </motion.button>

        <h1 className="text-lg font-semibold text-white">Voice Memos</h1>

        <div className="w-12" />
      </div>

      {/* Recording Interface (Top) */}
      <div className="flex flex-col items-center justify-center border-b border-white/10 px-6 pb-8 pt-4">
        {/* Waveform Visualization */}
        <div className="mb-6 flex h-32 w-full items-center justify-center gap-1">
          {selectedMemo ? (
            <div className="flex h-full items-center gap-[2px]">
              {generateWaveform(selectedMemo).map((bar, index) => {
                const isPast = index / 40 < currentTime / duration;
                return (
                  <motion.div
                    key={index}
                    className={`w-1 rounded-full transition-all duration-100 ${
                      isPast ? "bg-white" : "bg-white/20"
                    }`}
                    style={{ height: `${bar.height}%` }}
                    animate={
                      isPlaying && isPast
                        ? {
                            scaleY: [1, 1.2, 1],
                          }
                        : {}
                    }
                    transition={{
                      repeat: Infinity,
                      duration: 0.3,
                      delay: index * 0.01,
                    }}
                  />
                );
              })}
            </div>
          ) : (
            <div className="flex items-center gap-1">
              {Array.from({ length: 20 }).map((_, i) => (
                <div
                  key={i}
                  className="w-1 rounded-full bg-white/20"
                  style={{ height: `${20 + Math.random() * 60}%` }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Current Recording Info */}
        {selectedMemo ? (
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-white">
              {memos.find((m) => m._id === selectedMemo)?.title || "Untitled"}
            </h2>
            <p className="mt-1 text-sm text-white/50">
              {formatDuration(currentTime)} /{" "}
              {formatDuration(memos.find((m) => m._id === selectedMemo)?.duration || 0)}
            </p>
          </div>
        ) : (
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-white">Select a Memo</h2>
            <p className="mt-1 text-sm text-white/50">Tap any recording below to play</p>
          </div>
        )}

        {/* Playback Controls */}
        <div className="mt-8 flex items-center gap-8">
          <motion.button
            whileTap={{ scale: 0.9 }}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white/50"
          >
            <SkipBack className="h-6 w-6" />
          </motion.button>

          {selectedMemo && (
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const memo = memos.find((m) => m._id === selectedMemo);
                if (memo) togglePlay(memo);
              }}
              className="flex h-20 w-20 items-center justify-center rounded-full bg-white shadow-lg"
            >
              {isPlaying ? (
                <Pause className="h-8 w-8 text-black" />
              ) : (
                <Play className="ml-1 h-8 w-8 text-black" />
              )}
            </motion.button>
          )}

          <motion.button
            whileTap={{ scale: 0.9 }}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white/50"
          >
            <SkipForward className="h-6 w-6" />
          </motion.button>
        </div>
      </div>

      {/* Memos List */}
      <div className="flex-1 overflow-y-auto">
        {memos.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-500">
              <Mic className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-white">No Voice Memos</h2>
            <p className="mt-1 text-sm text-white/50">
              Recorded memos will appear here
            </p>
          </div>
        ) : (
          <div className="divide-y divide-white/10">
            {memos.map((memo, index) => {
              const isSelected = selectedMemo === memo._id;
              const formattedDate = format(
                new Date(memo.createdAt),
                "MMM d, yyyy"
              );

              return (
                <motion.div
                  key={memo._id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => setSelectedMemo(memo._id)}
                  className={`flex cursor-pointer items-center justify-between px-4 py-3 transition-colors hover:bg-white/5 ${
                    isSelected ? "bg-white/10" : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {/* Play Icon or Number */}
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-full ${
                        isSelected ? "bg-red-500" : "bg-white/10"
                      }`}
                    >
                      {isSelected && isPlaying ? (
                        <div className="flex gap-[2px]">
                          <motion.div
                            animate={{ scaleY: [1, 0.5, 1] }}
                            transition={{ repeat: Infinity, duration: 0.5 }}
                            className="h-3 w-1 bg-white"
                          />
                          <motion.div
                            animate={{ scaleY: [0.5, 1, 0.5] }}
                            transition={{ repeat: Infinity, duration: 0.5 }}
                            className="h-3 w-1 bg-white"
                          />
                          <motion.div
                            animate={{ scaleY: [1, 0.5, 1] }}
                            transition={{ repeat: Infinity, duration: 0.5 }}
                            className="h-3 w-1 bg-white"
                          />
                        </div>
                      ) : (
                        <span className="text-sm font-medium text-white">
                          {index + 1}
                        </span>
                      )}
                    </div>

                    {/* Info */}
                    <div>
                      <h3 className="text-base font-medium text-white">
                        {memo.title}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-white/50">{formattedDate}</span>
                        <span className="text-xs text-white/30">•</span>
                        <span className="text-xs text-white/50">
                          {formatDuration(memo.duration)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {memo.isFavorite && (
                      <Heart className="h-4 w-4 fill-red-500 text-red-500" />
                    )}
                    <motion.button
                      whileTap={{ scale: 0.9 }}
                      className="p-2 text-white/50 hover:text-white"
                    >
                      <MoreHorizontal className="h-5 w-5" />
                    </motion.button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Home Indicator */}
      <div className="pb-2 pt-1">
        <div className="mx-auto h-1 w-32 rounded-full bg-white/30" />
      </div>
    </motion.div>
  );
}

// Add Mic icon since it's missing from imports
import { Mic } from "lucide-react";